import { NgModule, Optional, SkipSelf } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
// Interfaces
import { IAppDataStore } from './interfaces/app-data-store.abstract.service';
import { IAuthenticationService } from './interfaces/authentication.abstract.service';
import { IAuthorizationService } from './interfaces/authorization.abstract.service';
import { IContextService } from './interfaces/context.abstract.service';
import { INavigationService } from './interfaces/navigation.abstract.service';
import { IOrgContextService } from './interfaces/org-context.abstract.service';
import { IStorageService } from './interfaces/storage.abstract.service';
import { ISettingsService } from './interfaces/settings.abstract.service';
// Services
import { LogService } from './services/log.service';
import { PershingTranslationService } from './services/pershing-translation.service';
import { AppDataStore } from './services/app-data-store.service';
import { ContextService } from './services/context.service';
import { OrgContextService } from './services/org-context.service';
import { AuthenticationService } from './services/authentication.service';
import { PermissionCheckerService } from './services/permission-checker.service';
import { StorageService } from './services/storage.service';
import { UserSettingsService } from './services/user.settings.service';
import { DefaultNavigationService } from './services/implementation/default.navigation.service';
import { PershingTranslateLoader } from './services/pershing-translate-loader.service';
// Pipes
import { PershingDecimalFormatPipe } from './pipes/pershing-decimal-format.pipe';
import { PershingDateFormatPipe } from './pipes/pershing-date-format.pipe';
import { PershingPhoneFormatPipe } from './pipes/pershing-phone-format.pipe';
import { PershingCurrencyAbbreviationPipe } from './pipes/pershing-currency-abbreviation.pipe';
import { PershingSanitizePipe } from './pipes/pershing-sanitize.pipe';
import { PershingSafePipe } from './pipes/pershing-safe.pipe';
import { PershingTranslatePipe } from './pipes/pershing-translate.pipe';
// Utils
import { ServiceRouter } from './utils/service-router';
import { JwtHeaderInterceptor, EsfHeaderInterceptor, SensitiveRequestHeadersInterceptor } from './utils/interceptors';
// Third party packages
import { ResponsiveModule } from 'ngx-responsive';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { DndModule } from 'ng2-dnd';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const PERSHING_SERVICES = [
    LogService,
    PershingTranslationService,
    { provide: IAppDataStore, useClass: AppDataStore},
    { provide: IContextService, useClass: ContextService },
    { provide: IOrgContextService, useClass: OrgContextService},
    { provide: IAuthenticationService, useClass: AuthenticationService },
    { provide: INavigationService, useClass: DefaultNavigationService },
    { provide: IAuthorizationService, useClass: PermissionCheckerService },
    { provide: ISettingsService, useClass: UserSettingsService },
    { provide: IStorageService, useClass: StorageService }
];

const PERSHING_PIPES = [
    PershingDecimalFormatPipe,
    PershingDateFormatPipe,
    PershingPhoneFormatPipe,
    PershingCurrencyAbbreviationPipe,
    PershingSanitizePipe,
    PershingSafePipe,
    PershingTranslatePipe
];

export const PERSHING_CORE_PROVIDERS: any[] = [
    ServiceRouter,
    ...PERSHING_SERVICES,
    ...PERSHING_PIPES,
    { provide: HTTP_INTERCEPTORS, useClass: JwtHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EsfHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SensitiveRequestHeadersInterceptor, multi: true },
];

/**
 * PershingCoreModule is to only be imported in Feature Modules!
 */
@NgModule({
    imports: [
        ResponsiveModule,
        FormsModule,
        CommonModule,
        TranslateModule,
        DndModule
    ],
    declarations: [
        ...PERSHING_PIPES,
    ],
    exports: [
        ResponsiveModule,
        FormsModule,
        TranslateModule,
        CommonModule,
        DndModule,
        ...PERSHING_PIPES
    ]
})
export class PershingCoreModule {}

/**
 * PershingCoreRootModule should only be imported in Application Root Module!
 */
@NgModule({
    imports: [
        PershingCoreModule,
        HttpClientModule,
        ResponsiveModule.forRoot(),
        TranslateModule.forRoot({
            loader: { provide: TranslateLoader, useClass: PershingTranslateLoader }
        }),
        DndModule.forRoot()
    ],
    providers: [
        ...PERSHING_CORE_PROVIDERS
    ],
    exports: [
        PershingCoreModule
    ]
})
export class PershingCoreRootModule {
    constructor(@Optional() @SkipSelf() parentModule: PershingCoreRootModule) {
        if (parentModule) {
            throw new Error('PershingCoreModule is already loaded. Import it in the AppModule only');
        }
    }
}
